'use strict';
import get from 'lodash/get';
import isFunction from 'lodash/isFunction';
import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import isUndefined from 'lodash/isUndefined';
import mapValues from 'lodash/mapValues';
import omit from 'lodash/omit';
import pickBy from 'lodash/pickBy';
import React from 'react';
import ReactDOM from 'react-dom';
import {Observable} from 'rxjs/Observable';
import {Subject} from 'rxjs/Subject';
import * as actions from '../../system/actions/notificationsActions';
import {NoteState} from '../../system/enums/NoteState';
import {getNotifications} from '../../system/reducers';
import {toArray} from '../filters/filterUtils';
import {AudioNotificationService} from './AudioNotificationService';
import {NotificationPanel} from './components/NotificationPanel';


const ON_COMPLETE_REMOVAL_DELAY = 1500; // time after which to automatically remove the item from the list when it is complete
const REMOVAL = {ALWAYS: 0, DESKTOP: 1, NEVER: 2};

export class NotificationService {
  static SERVICE_NAME = 'NotificationService';

  static $inject = [
    '$document',
    '$ngRedux',
    'DeviceServices',
    '$toast',
    '$ionicPlatform',
    'IONIC_BACK_PRIORITY',
    AudioNotificationService.SERVICE_NAME,
  ];

  constructor(
    $document,
    $ngRedux,
    DeviceServices,
    $toast,
    $ionicPlatform,
    IONIC_BACK_PRIORITY,
    audioNotificationService,
  ) {
    this.$document = $document;
    this.$ngRedux = $ngRedux;
    this.DeviceServices = DeviceServices;
    this.$toast = $toast;
    this.$ionicPlatform = $ionicPlatform;
    this.IONIC_BACK_PRIORITY = IONIC_BACK_PRIORITY;
    this.audioNotificationService = audioNotificationService;
    this.ids = 0;
    this.show = false;
    this.notesMap = {};
    this.notes = [];
    this.REMOVAL = REMOVAL;

    this.show$ = new Subject();
    this.show$
      .filter(show => show)
      .switchMap(() =>
        new Observable(observer =>
          // TODO(ionic): replace this service as we need to keep the functionality after removing ionic
          this.$ionicPlatform.registerBackButtonAction(
            observer.next.bind(observer),
            this.IONIC_BACK_PRIORITY.popup
          ))
          .takeUntil(this.show$.filter(show => !show))
      )
      .subscribe(() => this.hideNotifications());
  }

  getNotification(noteId) {
    return this.__findNote(noteId);
  }

  // return a id for removal
  addNotification(note, state) {
    const newId = this.ids++;
    let nextNote;
    if (isObject(note.progress) && isFunction(note.progress.subscribe)) {
      this.__setNotificationProgress({__noteId: newId}, note.progress);
      nextNote = omit(note, 'progress');
    } else {
      nextNote = {
        ...note,
      };
    }
    nextNote.__noteId = newId;
    nextNote.__noteState = !isUndefined(state) ? state : NoteState.INDEFINITE;
    this.__bindActions(nextNote);

    if (nextNote.promise)
      this.__setNotificationPromise(nextNote, nextNote.promise);


    this.__updateState({
      ...this.notesMap,
      [newId]: nextNote,
    });

    return newId;
  }

  /**
   * Use to make basic changes to note props, such as renaming, updating the actions object, etc.
   * @param noteId
   * @param data
   */
  updateNotification(noteId, data) {
    let note;
    if (isNumber(noteId)) {
      note = this.__findNote(noteId);
    } else {
      throw new Error('Notification is invalid!', noteId);
    }

    if (!note) {
      throw new Error('Notification ' + noteId + ' does not exist!', note);
    }

    const nextNote = {
      ...note,
      ...data,
    };
    if (data.actions) {
      this.__bindActions(nextNote);
    }
    this.__updateState({
      ...this.notesMap,
      [noteId]: nextNote,
    });
  }

  reportError(note) {
    const newId = this.ids++;
    note.__noteId = newId;
    note.__noteState = NoteState.ERROR;
    this.__bindActions(note);
    this.__playAudio(note);
    this.__showToast(note);

    this.__updateState({
      ...this.notesMap,
      [newId]: note,
    });

    return note.__noteId;
  }

  notificationExists(noteId) {
    return !!this.__findNote(noteId);
  }

  // this most likely needs to reset the notification to INDEFINITE_PROGRESS
  updateNotificationPromise(noteId, promise) {
    let note;
    if (isNumber(noteId)) {
      note = this.__findNote(noteId);
    } else if (isObject(noteId) && isNumber(noteId.__noteId)) {
      note = this.__findNote(noteId.__noteId);
    } else {
      throw new Error('Notification is invalid!', noteId);
    }

    if (!note) {
      throw new Error('Notification ' + noteId + ' does not exist!', noteId);
    }

    const nextNote = {
      ...note,
    };
    this.__setNotificationPromise(nextNote, promise);
    this.__updateState({
      ...this.notesMap,
      [note.__noteId]: nextNote,
    });
  }

  updateNotificationProgress(noteId, progressObservable) {
    let note;
    if (isNumber(noteId)) {
      note = this.__findNote(noteId);
    } else if (isObject(noteId) && isNumber(noteId.__noteId)) {
      note = this.__findNote(noteId.__noteId);
    } else {
      throw new Error('Notification is invalid!', noteId);
    }

    if (!note) {
      throw new Error('Notification ' + noteId + ' does not exist!', noteId);
    }

    const nextNote = {
      ...note,
    };
    this.__setNotificationProgress(nextNote, progressObservable);
    this.__updateState({
      ...this.notesMap,
      [note.__noteId]: nextNote,
    });
  }

  __setNotificationPromise(note, promise) {
    note.promise = promise;
    promise.then(
      this.__createOnNoteComplete(note, promise),
      this.__createOnNoteError(note, promise),
      this.__createOnNoteProgress(note, null, promise));
  }

  __createOnNoteComplete(note, promise) {
    const id = note.__noteId;
    return response => {
      const currentNote = this.__findNote(id);
      // cancel if the promise has been changed.
      if (!currentNote || promise !== currentNote.promise)
        return;

      const nextNote = {
        ...currentNote,
        __noteState: NoteState.COMPLETE,
        name: currentNote.completeName ? currentNote.completeName : currentNote.name,
        message: isString(currentNote.completeMessage) ? currentNote.completeMessage : null,
      };

      if (isFunction(currentNote.getNameFromState)) {
        nextNote.name = currentNote.getNameFromState(currentNote, nextNote);
      }

      this.__playAudio(nextNote);
      this.__showToast(nextNote);

      this.__updateState({
        ...this.notesMap,
        [id]: nextNote,
      });

      if (currentNote.removal === REMOVAL.ALWAYS
        || (!this.DeviceServices.isWebView() && currentNote.removal === REMOVAL.DESKTOP)
      ) {
        setTimeout(() => this.removeNotification(id), ON_COMPLETE_REMOVAL_DELAY);
      }
    };
  }

  __createOnNoteError(note, promise) {
    const id = note.__noteId;
    return error => {
      const currentNote = this.__findNote(id);
      // cancel if the promise has been changed.
      if (!currentNote || promise !== currentNote.promise)
        return;

      const nextNote = {
        ...currentNote,
        __noteState: NoteState.ERROR,
        // TODO: get this from error.message. ALL SOURCES feeding errors that may end up in the notification panel MUST be scrubbed for appropriate human friendly error messages on client and server
        message: currentNote.errorMessage ? currentNote.errorMessage : 'An error occurred',
      };

      if (isFunction(currentNote.getNameFromState)) {
        nextNote.name = currentNote.getNameFromState(currentNote, nextNote);
      }

      this.__playAudio(nextNote);
      this.__showToast(nextNote);

      this.__updateState({
        ...this.notesMap,
        [id]: nextNote,
      });
    };
  }

  __removeToastId(noteId) {
    const currentNote = this.__findNote(noteId);
    // cancel if the promise has been changed.
    if (!currentNote || isUndefined(currentNote.__toastId))
      return;

    this.__updateState({
      ...this.notesMap,
      [noteId]: omit(currentNote, '__toastId'),
    });
  }

  __createOnNoteProgress(note, promise) {
    const id = note.__noteId;
    return progress => {
      const currentNote = this.__findNote(id);
      // cancel if the promise has been changed. Skip if created from an observable (promise == null)
      if (!currentNote || (promise !== null && promise !== currentNote.promise))
        return;

      const nextNote = {
        ...currentNote,
        __noteState: NoteState.PROGRESS,
      };

      if (isNumber(progress)) {
        nextNote.progress = progress;

      } else if (!isUndefined(progress.lengthComputable)) { // instanceof ProgressEvent
        if (progress.lengthComputable)
          nextNote.progress = Math.floor(progress.loaded / progress.total * 1000) / 10;
        else
          nextNote.__noteState = NoteState.INDEFINITE;

      } else if (isNumber(progress.progress)) {
        nextNote.progress = progress.progress;

      } else if (!isNumber(nextNote.progress)) {
        nextNote.__noteState = NoteState.INDEFINITE;
      }

      if (isFunction(currentNote.getNameFromProgress)) {
        nextNote.name = currentNote.getNameFromProgress(currentNote, nextNote);
      }

      if (currentNote.__noteState !== nextNote.__noteState || currentNote.progress !== nextNote.progress) {
        this.__updateState({
          ...this.notesMap,
          [id]: nextNote,
        });
      }
    };
  }

  __setNotificationProgress(note, progressObservable) {
    progressObservable.subscribe(this.__createOnNoteProgress(note, null));
  }

  removeNotification(id) {
    if (this.notificationExists(id)) {
      this.__updateState(pickBy(this.notesMap, note => this.__dismissToast(note, note.__noteId !== id)));
    }
  }

  sweep() {
    // eslint-disable-next-line max-len
    this.__updateState(pickBy(this.notesMap, note => this.__dismissToast(note, note.__noteState !== NoteState.COMPLETE && note.__noteState !== NoteState.ERROR)));
  }

  __playAudio(note) {
    const state = note.__noteState;
    if (get(note.audio, state)) {
      switch (state) {
        case NoteState.COMPLETE:
          // this.audioNotificationService.playPositive();
          break;

        case NoteState.ERROR:
          // this.audioNotificationService.playNegative();
          break;
      }
    }
  }

  __showToast(note) {
    const state = note.__noteState;
    if (!this.show && note.toasts && note.toasts[state] && !note.__toastId) {
      const noteId = note.__noteId;
      note.__toastId = this.$toast.showMessageSync(note.message, note.name, NotificationService.__getToastType(state), {
        action: note.actions && note.actions[state],
        onCloseButton: () => this.removeNotification(noteId),
        toastOptions: {
          onClose: () => this.__removeToastId(noteId),
        },
      });
    }
  }

  static __getToastType(noteState) {
    switch (noteState) {
      case NoteState.ERROR:
        return 'error';
      case NoteState.COMPLETE:
        return 'success';
      case NoteState.INFO:
        return 'info';
    }
  }

  __dismissToast(note, condition) {
    if (!condition && !isUndefined(note.__toastId)) {
      this.$toast.dismiss(note.__toastId);
    }

    return condition;
  }

  showNotifications() {
    this.__updateShow(true);
  }

  hideNotifications() {
    this.__updateShow(false);
  }

  showButton() {
    const noteState = getNotifications(this.$ngRedux.getState());
    if (!noteState.showButton) {
      this.$ngRedux.dispatch(actions.showButton());
    }
    this.body.addClass('processing-queue-btn');
  }

  hideButton() {
    const noteState = getNotifications(this.$ngRedux.getState());
    if (noteState.showButton) {
      this.$ngRedux.dispatch(actions.hideButton());
    }
    this.body.removeClass('processing-queue-btn');
  }

  __updatePulse(state) {
    const noteState = getNotifications(this.$ngRedux.getState());
    if (noteState.state !== state) {
      this.$ngRedux.dispatch(actions.updateState({state}));
    }

    this.body.removeClass('processing-error processing-active processing-complete');
    switch (state) {
      case NoteState.ERROR:
        this.body.addClass('processing-error');
        break;
      case NoteState.COMPLETE:
        this.body.addClass('processing-complete');
        break;
      case NoteState.PROGRESS:
      case NoteState.INDEFINITE:
        this.body.addClass('processing-active');
        break;
    }
  }

  __bindActions(note) {
    if (note.actions) {
      const noteId = note.__noteId;
      const sourceActions = note.actions;
      note.actions = mapValues(sourceActions, action => {
        if (action.handler && action.handler.__wrappedNoteAction)
          return action;

        const sourceHandler = action.handler;
        const wrapperHandler = () => {
          this.removeNotification(noteId);
          if (isFunction(sourceHandler)) {
            sourceHandler();
          }
        };
        wrapperHandler.__wrappedNoteAction = true;

        return {
          ...action,
          handler: wrapperHandler,
        };
      });
    }
  }

  init() {
    this.body = this.$document.find('body');

    this.reactProps = {
      handleSweep: this.sweep.bind(this),
      handleRemove: (ev, note) => note && this.removeNotification(note.__noteId),
      handleShow: () => this.__updateShow(true),
      handleHide: () => this.__updateShow(false),
    };

    // this.__generateDummyData();

    this.node = document.createElement('div');
    document.body.appendChild(this.node);
    this.__render(this.notes, this.show);
  }

  __updateShow(show) {
    if (this.show !== show) {
      this.show = show;
      if (this.node) {
        this.show$.next(show);

        if (show) {
          this.$toast.dismiss();
        }

        this.__render(this.notes, this.show);
      }
    }
  }

  __render(notes, show) {
    if (this.node && show) {
      ReactDOM.render((
        <NotificationPanel
          className="NotificationPanel_position"
          list={notes}
          show={show}
          onSweep={this.reactProps.handleSweep}
          onRemove={this.reactProps.handleRemove}
          onShow={this.reactProps.handleShow}
          onHide={this.reactProps.handleHide}
        />
      ), this.node);
      this.isMounted = true;
    } else if (this.node && this.isMounted) {
      // Required to prevent Safari from displaying the off-screen panel when the keyboard is open
      ReactDOM.unmountComponentAtNode(this.node);
      this.isMounted = false;
    }
  }

  __findNote(id) {
    return this.notesMap[id];
  }

  __updateState(notesMap) {
    const nextNotes = toArray(notesMap);
    const size = nextNotes.length;
    const noteState = getNotifications(this.$ngRedux.getState());
    if (noteState.size !== size) {
      this.$ngRedux.dispatch(actions.updateSize({size}));
    }

    if (size === 0) {
      this.hideNotifications();
      this.hideButton();
      this.__updatePulse(null);

    } else {
      this.showButton();

      // update the sort order
      NotificationService.__sortNotes(nextNotes);

      this.__updatePulse(nextNotes[0].__noteState);
    }

    this.notesMap = notesMap;
    this.notes = nextNotes;
    this.__render(nextNotes, this.show);
  }

  static __sortNotes(notes) {
    notes.sort(NotificationService.__notesComparator);
  }

  static __notesComparator(n1, n2) {
    return n1.__noteState - n2.__noteState;
  }

  // for testing purposes
  __generateDummyData() {
    const ERROR = NoteState.ERROR;
    const COMPLETE = NoteState.COMPLETE;
    const notes = {
      [this.ids]: {name: 'a complete one', message: 'Complete', __noteId: this.ids++, __noteState: COMPLETE},
      [this.ids]: {name: 'a prog one', progress: 15, __noteId: this.ids++, __noteState: NoteState.PROGRESS},
      [this.ids]: {name: 'a def one', __noteId: this.ids++, __noteState: NoteState.INDEFINITE},
      [this.ids]: {name: 'a err one', message: 'Life: Stuff breaks', __noteId: this.ids++, __noteState: ERROR},
      [this.ids]: {name: 'a com 2 one', message: 'We\'re done here', __noteId: this.ids++, __noteState: COMPLETE},
      [this.ids]: {name: 'a prog 2 one', progress: 39, __noteId: this.ids++, __noteState: NoteState.PROGRESS},
    };

    setTimeout(() => {
      this.__updateState(notes);
      this.reportError({
        name: 'a err 2 one',
        message: 'W\'re Sorry you\'re call could not be completed as dialed.',
        toasts: {
          [ERROR]: true,
        },
      });
    }, 10000);
  }
}




// WEBPACK FOOTER //
// ./src/js/core-utilities/notifications/NotificationService.js